import { useState } from 'react';
import { toast } from 'react-toastify';
import { PATHS } from 'src/common/constants';
import {
  useNotesAPI,
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import {
  DeleteSuiteResult,
  FetchMoreDetailsResult,
  FetchSuiteResult,
  useSuiteAPI,
  UseSuiteAPIReturn,
} from 'src/SharedCustomHooks/UseSuiteAPI/useSuiteAPI';
import {
  FetchPropertyManagerDetailsResult,
  usePropertiesAPI,
  UsePropertiesAPIReturn,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import {
  WalkThrough,
  Property,
  Suite as SuiteType,
  Counts,
  Contact,
} from 'src/SharedCustomHooks/UseSuiteActions/types';
import { ModulePermission } from 'src/components/ContactForm/types';
import { useSaveSuite } from 'src/SharedCustomHooks/UseSaveSuite/useSaveSuite';

export interface UseSuitePageDataParams {
  match: { params: { id: string } };
  location: any;
  history: any;
  branding: any;
  user: any;
  userRole: any;
}

export interface UseSuitePageDataReturn {
  suite: SuiteType | null;
  counts: Counts | null;
  walkThroughs: WalkThrough[];
  properties: Property[];
  property: Property | null;
  propertyManagerDetails: Contact | null;
  allowedTabList: ModulePermission[];
  refreshKeyDatesTrigger: number;

  notesAPI: UseNotesAPIReturn;
  isMutating: boolean;
  suiteIdParts: string[];

  fetchSuiteData: () => Promise<void>;
  refreshSuiteCounts: () => Promise<void>;
  fetchServiceRequestData: () => Promise<void>;

  saveSuite: (
    suiteData: any,
    suiteAreas: any,
    floorPlanImage: any
  ) => Promise<{ ok: boolean }>;
  deleteUnit: (unitId: string) => Promise<void>;
  toggleMobileSync: () => void;
  fetchMoreDetails: () => Promise<any | null>;
  reRenderKeyPickupPdf: () => Promise<void>;
}

const isHomeOwnerOrOccupant = (someUser: any): boolean =>
  !!(
    someUser?.roles?.length > 0 &&
    ['Home Owner', 'Occupant'].includes(someUser.roles[0].role.name)
  );

export const useSuitePageData = ({
  match,
  location,
  history,
  user,
  userRole,
}: UseSuitePageDataParams): UseSuitePageDataReturn => {
  const suiteAPI: UseSuiteAPIReturn = useSuiteAPI();
  const propertiesAPI: UsePropertiesAPIReturn = usePropertiesAPI();

  const { saveSuite: persistSuite } = useSaveSuite();
  const notesAPI: UseNotesAPIReturn = useNotesAPI(user);

  const [refreshKeyDatesTrigger, setRefreshKeyDatesTrigger] =
    useState<number>(0);

  const suiteIdParts: string[] = match.params.id.split('&');
  const serviceRequestId: string | null =
    location.state?.data?.serviceRequestId || null;

  // BOS legacy magic number shii - todo: Fix this.
  const propertyId: string =
    suiteIdParts.length === 3 ? suiteIdParts[2] : suiteIdParts[0];

  const property: Property | null =
    propertiesAPI.properties.find(
      (entry: Property) => entry.id === propertyId
    ) || null;

  const propertyManagerDetails: Contact | null =
    propertiesAPI.propertyManagerDetails
      ? {
          ...propertiesAPI.propertyManagerDetails,
          ...(propertiesAPI.calendlyData || {}),
        }
      : null;

  const fetchSuiteData = async (): Promise<void> => {
    try {
      const suiteId: string = suiteIdParts[1];

      const suiteResult: FetchSuiteResult =
        await suiteAPI.fetchSuiteById(suiteId);
      await suiteAPI.fetchSuiteCounts(suiteId, userRole);
      await propertiesAPI.fetchProperties(user.id, userRole);
      await suiteAPI.fetchSuiteWalkThroughs(suiteId, userRole);
      await propertiesAPI.fetchAllowedTabList(propertyId);

      if (isHomeOwnerOrOccupant(user)) {
        const managerResult: FetchPropertyManagerDetailsResult =
          await propertiesAPI.fetchPropertyManagerDetailsForHomeowner(
            propertyId
          );

        if (managerResult.propertyManagerDetails?.email) {
          await propertiesAPI.fetchCalendlyLink(
            managerResult.propertyManagerDetails.email
          );
        }
      }

      if (suiteResult.suite?.id) {
        await notesAPI.fetchNotes(suiteResult.suite.id);
      }
    } catch (err: any) {
      console.error('❌ Error fetching suite data:', err);
      toast.error(`🚫 ${err.message || 'Failed to load suite data'}`);
      throw err;
    }
  };

  const refreshSuiteCounts = async (): Promise<void> => {
    const suiteId: string | undefined = suiteAPI.suite?.id;

    if (!suiteId) {
      return;
    }

    await suiteAPI.fetchSuiteCounts(suiteId, userRole);
  };

  const fetchServiceRequestData = async (): Promise<void> => {
    if (!serviceRequestId) {
      return;
    }

    const serviceRequestPropertyId: string = suiteIdParts[2];

    if (serviceRequestPropertyId) {
      await propertiesAPI.fetchPropertySuites(
        serviceRequestPropertyId,
        user.id,
        userRole
      );
    }
  };

  const saveSuite = async (
    suiteData: any,
    suiteAreas: any,
    floorPlanImage: any
  ): Promise<{ ok: boolean }> => {
    if (!suiteData.id) {
      console.warn('saveSuite called with no suiteData.id');
      return { ok: false };
    }

    const { ok } = await persistSuite(suiteData, suiteAreas, floorPlanImage);

    if (!ok) {
      return { ok };
    }

    await suiteAPI.fetchSuiteById(suiteData.id);
    await suiteAPI.fetchSuiteCounts(suiteData.id, userRole);

    setRefreshKeyDatesTrigger((prev: number) => prev + 1);

    return { ok };
  };

  const deleteUnit = async (unitId: string): Promise<void> => {
    const result: DeleteSuiteResult = await suiteAPI.deleteSuite(
      unitId,
      user.id
    );

    if (!result.ok) {
      return;
    }

    const propertyFromState: Property | undefined = location.state?.property;

    if (history && propertyFromState) {
      history.push({
        pathname: PATHS.PROPERTIES_VIEW.replace(':id', propertyFromState.id),
        state: { data: propertyFromState },
      });
    }
  };

  const toggleMobileSync = (): void => {
    if (!suiteAPI.suite) {
      console.warn('toggleMobileSync called with no suite loaded');
      return;
    }

    suiteAPI.setSuite({
      ...suiteAPI.suite,
      mobileSyncAllowed: !suiteAPI.suite.mobileSyncAllowed,
    });
  };

  const fetchMoreDetails = async (): Promise<any | null> => {
    const suiteId: string | undefined = suiteAPI.suite?.id;

    if (!suiteId) {
      return null;
    }

    const result: FetchMoreDetailsResult =
      await suiteAPI.fetchMoreDetails(suiteId);
    return result.details;
  };

  const reRenderKeyPickupPdf = async (): Promise<void> => {
    const suiteId: string | undefined = suiteAPI.suite?.id;

    if (!suiteId) {
      return;
    }

    await suiteAPI.reRenderKeyPickupPdf(suiteId);
  };

  const isMutating: boolean =
    suiteAPI.isUpdatingSuite ||
    suiteAPI.isDeletingSuite ||
    suiteAPI.isReRenderingKeyPickupPdf;

  return {
    suite: suiteAPI.suite,
    counts: suiteAPI.counts,
    walkThroughs: suiteAPI.walkThroughs,
    properties: propertiesAPI.properties,
    property,
    propertyManagerDetails,
    allowedTabList: propertiesAPI.allowedTabList,
    refreshKeyDatesTrigger,

    notesAPI,
    isMutating,
    suiteIdParts,

    fetchSuiteData,
    refreshSuiteCounts,
    fetchServiceRequestData,

    saveSuite,
    deleteUnit,
    toggleMobileSync,
    fetchMoreDetails,
    reRenderKeyPickupPdf,
  };
};
